import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import { useSidebar } from '../../contexts/SidebarContext'
import { useAuth } from '../../hooks/useAuth'
import { profileService, type Profile } from '../../services/profileService'
import Logo from '../common/Logo'
import WheelLoader from '../common/WheelLoader'
import Sidebar from './Sidebar'

export default function AuthenticatedLayout() {
  const { isAuthenticated, loading } = useAuth()
  const navigate = useNavigate()
  const { isOpen: sidebarOpen, open: openSidebar, close: closeSidebar, isCollapsed } = useSidebar()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)

  useEffect(() => {
    const fetchProfile = async () => {
      if (isAuthenticated) {
        try {
          const data = await profileService.getMyProfile()
          setProfile(data)
        } catch {
          setProfile(null)
        }
      }
      setProfileLoading(false)
    }

    if (!loading) {
      fetchProfile()
    }
  }, [isAuthenticated, loading])

  // Redirect to landing if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/')
    }
  }, [loading, isAuthenticated, navigate])

  if (loading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <WheelLoader width={100} />
          </div>
          <p className="text-black/60">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="h-screen flex flex-col lg:block">
      {/* Desktop Sidebar - Fixed */}
      <Sidebar profile={profile} />

      {/* Mobile Sidebar - Slide-in */}
      <Sidebar profile={profile} isOpen={sidebarOpen} onClose={closeSidebar} />

      {/* Mobile Header - Only visible on mobile */}
      <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-white/80 backdrop-blur-md border-b border-black/10 sticky top-0 z-30">
        <button
          onClick={openSidebar}
          className="p-2 -ml-2 rounded-lg text-black/60 hover:text-black hover:bg-black/5 transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <Logo size="lg" to="/home" />
      </header>

      {/* Main Content Area - Scrollable */}
      <main
        className={`flex-1 lg:h-full overflow-y-auto transition-all duration-300 ease-in-out ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}
      >
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet context={{ profile, setProfile }} />
        </div>
      </main>
    </div>
  )
}

// Hook to access profile from child components
export function useProfileContext() {
  return useOutletContext<{
    profile: Profile | null
    setProfile: (profile: Profile | null) => void
  }>()
}
